import {
  WSAlert,
  WSAvatarProps,
  WSList,
  WSMarginProps,
  WSText
} from "@wingspanhq/fe-component-library";
import { useMemo } from "react";

import { CollapsibleItem } from "../../../../shared/components/CollapsibleItem/CollapsibleItem";

import { RequirementContextType } from "../../types";
import { ISharedFileRequestJobResponse } from "@wingspanhq/shared-files/dist/lib/interfaces/api/sharedFileRequestJob";
import {
  ISharedFileRequestSettingsJob,
  SharedFileRequestJobStatus
} from "@wingspanhq/shared-files/dist/lib/interfaces/sharedFileRequestJob";

/**
 * A check that did not pass but does not block — an insurance import or monitor
 * job in its `Warning` status. It is its own state here rather than a passed
 * check with a footnote: the contractor may proceed past it, but it is not a
 * successful match and the client is asked to rule on it.
 */
export const VALIDATION_ITEM_WARNING_STATUS = "Warning" as const;

export type ValidationItemStatus =
  | SharedFileRequestJobStatus
  | typeof VALIDATION_ITEM_WARNING_STATUS;

export type ValidationItemProps = (
  | (Omit<ISharedFileRequestJobResponse, "status"> & {
      status?: ValidationItemStatus;
    })
  | ISharedFileRequestSettingsJob
) & {
  warningReason?: string;
  statusSummary?: string;
  groupKey?: string;
};

type Props = {
  item: ValidationItemProps;
  contextType: RequirementContextType;
} & WSMarginProps;

export const ValidationItem: React.FC<Props> = ({
  item,
  contextType,
  ...marginProps
}) => {
  const { title, description } = item;

  // When we display the AI validation widget in the requirement details view as instructions, we will just
  // show the title and description of the job
  const status: ValidationItemStatus =
    (item as { status?: ValidationItemStatus }).status ||
    SharedFileRequestJobStatus.Created;
  const statusReason =
    (item as ISharedFileRequestJobResponse).statusReason || "";

  const warningReason = item.warningReason;
  const statusSummary = item.statusSummary;

  const avatarProps = useMemo(() => {
    const statusToPropsMap: Record<ValidationItemStatus, WSAvatarProps> =
      {
        [VALIDATION_ITEM_WARNING_STATUS]: {
          type: "icon",
          colorBackground: "amber300",
          color: "white",
          icon: "alert"
        },
        [SharedFileRequestJobStatus.Created]: {
          type: "icon",
          colorBackground: "gray300",
          color: "gray600",
          icon: "time"
        },
        [SharedFileRequestJobStatus.Pending]: {
          type: "icon",
          colorBackground: "gray300",
          color: "gray600",
          icon: "time"
        },
        [SharedFileRequestJobStatus.Processing]: {
          type: "icon",
          colorBackground: "gray50",
          color: "blue300",
          icon: "loader"
        },
        [SharedFileRequestJobStatus.Complete]: {
          type: "icon",
          colorBackground: "green400",
          color: "white",
          icon: "check-circle"
        },
        [SharedFileRequestJobStatus.Failed]: {
          type: "icon",
          colorBackground: "red400",
          color: "white",
          icon: "alert-circle"
        },
        [SharedFileRequestJobStatus.Error]: {
          type: "icon",
          colorBackground: "red400",
          color: "white",
          icon: "alert-circle"
        } // TODO: need to differentiate between Error and Failed
      };

    return statusToPropsMap[status];
  }, [status]);

  // expand the item if status is Failed OR Error OR Warning OR Complete with warnings
  const expanded =
    status === SharedFileRequestJobStatus.Failed ||
    status === SharedFileRequestJobStatus.Error ||
    status === VALIDATION_ITEM_WARNING_STATUS ||
    (status === SharedFileRequestJobStatus.Complete && !!warningReason);

  return (
    <CollapsibleItem
      avatarProps={avatarProps}
      title={title}
      isExpanded={expanded}
      {...marginProps}
      // subtitle={description}
    >
      <WSList gap="M">
        <WSText.ParagraphSm weight="book" color="gray600">
          {statusSummary || description}
        </WSText.ParagraphSm>

        {status === SharedFileRequestJobStatus.Complete && !statusSummary && (
          <WSAlert icon="check-circle" theme="success" title="Passed" />
        )}

        {statusReason && status === SharedFileRequestJobStatus.Failed && (
          <WSAlert icon="alert-circle" theme="error" title="Failed">
            {statusReason}
          </WSAlert>
        )}
        {statusReason && status === VALIDATION_ITEM_WARNING_STATUS && (
          <WSAlert icon="alert" theme="warning" title="Needs review">
            {statusReason}
          </WSAlert>
        )}
        {warningReason && (
          <WSAlert icon="alert" theme="warning" title="Risk signals">
            {warningReason}
          </WSAlert>
        )}
      </WSList>
    </CollapsibleItem>
  );
};
